import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { settingsApi, designerAccessApi } from '../services/gardenApi';
import { useGardenPageTitle } from '../useGardenPageTitle';

const HARDINESS_ZONES = [
  '1a','1b','2a','2b','3a','3b','4a','4b','5a','5b',
  '6a','6b','7a','7b','8a','8b','9a','9b','10a','10b','11a','11b','12','13',
];

export default function SettingsScreen() {
  useGardenPageTitle('Garden settings', 'Garden settings');
  const navigate = useNavigate();
  const [form, setForm] = useState({
    location_label: '',
    climate_zone: '',
    hemisphere: 'northern',
    show_watering_tasks: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await settingsApi.get();
      const s = r.settings || {};
      setForm({
        location_label: s.location_label || '',
        climate_zone: s.climate_zone || '',
        hemisphere: s.hemisphere === 'southern' ? 'southern' : 'northern',
        show_watering_tasks: s.show_watering_tasks !== false,
      });
    } catch (e) {
      setError(e.message || 'Could not load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    setError(null);
    try {
      const r = await settingsApi.put(patch);
      const s = r.settings || {};
      setForm({
        location_label: s.location_label || '',
        climate_zone: s.climate_zone || '',
        hemisphere: s.hemisphere === 'southern' ? 'southern' : 'northern',
        show_watering_tasks: s.show_watering_tasks !== false,
      });
      setSavedAt(Date.now());
      // Task badge cares about this when watering setting flips.
      window.dispatchEvent(new Event('garden:tasks-changed'));
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const savedRecently = savedAt && Date.now() - savedAt < 3000;

  return (
    <div className="relative pb-10">
      <header className="flex items-center gap-2 px-2 pt-2 pb-2">
        <button
          onClick={() => navigate(-1)}
          className="min-h-11 min-w-11 rounded-full flex items-center justify-center text-garden-ink"
          aria-label="Back"
        >
          <ArrowLeft size={22} />
        </button>
        <h1 className="text-lg font-semibold text-garden-ink">Garden settings</h1>
        {savedRecently ? (
          <span className="ml-auto mr-1 inline-flex items-center gap-1 text-xs text-garden-primary">
            <Check size={14} /> Saved
          </span>
        ) : null}
      </header>

      {error ? (
        <div className="mx-4 mb-3 p-3 rounded-xl bg-red-50 text-sm text-red-700">{error}</div>
      ) : null}

      {loading ? (
        <div className="px-4 py-6 text-sm text-garden-muted">Loading…</div>
      ) : (
        <div className="px-4 space-y-5">
          <Section
            title="Your garden"
            description="Used so the AI can give you advice that fits your region."
          >
            <Field label="Location label">
              <input
                type="text"
                value={form.location_label}
                onChange={(e) => setForm((f) => ({ ...f, location_label: e.target.value }))}
                onBlur={(e) => {
                  if (e.target.value !== (form.location_label || '')) return; // unchanged
                  save({ location_label: e.target.value });
                }}
                placeholder="e.g. Back yard, Portland OR"
                className="w-full min-h-11 px-3 rounded-xl bg-white border border-garden-ink/10 text-sm focus:outline-none focus:border-garden-primary"
              />
              <FieldHint>Tap away to save.</FieldHint>
            </Field>

            <Field label="USDA / hardiness zone">
              <select
                value={form.climate_zone}
                onChange={(e) => {
                  setForm((f) => ({ ...f, climate_zone: e.target.value }));
                  save({ climate_zone: e.target.value });
                }}
                className="w-full min-h-11 px-3 rounded-xl bg-white border border-garden-ink/10 text-sm focus:outline-none focus:border-garden-primary"
              >
                <option value="">{'\u2014 Not set \u2014'}</option>
                {HARDINESS_ZONES.map((z) => (
                  <option key={z} value={z}>Zone {z}</option>
                ))}
              </select>
            </Field>

            <Field label="Hemisphere">
              <div className="inline-flex bg-white border border-garden-ink/10 rounded-full p-0.5">
                {['northern', 'southern'].map((h) => {
                  const active = form.hemisphere === h;
                  return (
                    <button
                      key={h}
                      type="button"
                      disabled={saving}
                      onClick={() => save({ hemisphere: h })}
                      className={
                        'px-4 min-h-9 rounded-full text-xs font-medium transition ' +
                        (active
                          ? 'bg-garden-primary text-white'
                          : 'text-garden-muted')
                      }
                    >
                      {h === 'northern' ? 'Northern' : 'Southern'}
                    </button>
                  );
                })}
              </div>
              <FieldHint>
                Flips the seasons used for auto-generated tasks.
              </FieldHint>
            </Field>
          </Section>

          <Section
            title="Tasks"
            description="What auto-generated reminders should show up on the Tasks tab."
          >
            <ToggleRow
              label="Watering reminders"
              description="Monthly reminders during growing season for plants that need medium / high water."
              value={form.show_watering_tasks}
              disabled={saving}
              onChange={(v) => save({ show_watering_tasks: v })}
            />
          </Section>

          <DesignerAccessSection />
        </div>
      )}
    </div>
  );
}

// Lists every designer who has been granted co-edit access to this garden
// (via an accepted garden_handoff). Each row can be toggled on/off or fully
// revoked. When disabled, the backend rejects the designer's x-garden-as-user
// override so their co-edit UI immediately stops seeing this garden.
function DesignerAccessSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await designerAccessApi.list();
      setRows(Array.isArray(r.access) ? r.access : []);
    } catch (e) {
      setError(e.message || 'Could not load designer access');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (row, nextEnabled) => {
    setBusyId(row.id);
    setError('');
    try {
      await designerAccessApi.setEnabled(row.id, nextEnabled);
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, enabled: nextEnabled } : r)));
    } catch (e) {
      setError(e.message || 'Could not update access');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.designer_name}'s access? They will no longer be able to view or edit your garden.`)) return;
    setBusyId(row.id);
    setError('');
    try {
      await designerAccessApi.remove(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      setError(e.message || 'Could not remove access');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Section
      title="Designer access"
      description="Designers who sent you a prepopulated garden can co-edit it so long as access stays on. Turn access off at any time."
    >
      {error ? (
        <div className="p-2 rounded-lg bg-red-50 text-xs text-red-700">{error}</div>
      ) : null}
      {loading ? (
        <div className="text-xs text-garden-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-garden-muted">
          No designers have access yet. When a designer sends you a garden via
          an invite, they'll appear here.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <div key={row.id} className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-garden-ink truncate">
                  {row.designer_name}
                </div>
                {row.project_name ? (
                  <div className="text-xs text-garden-muted truncate">
                    {row.project_name}
                  </div>
                ) : null}
                {row.designer_email ? (
                  <div className="text-[11px] text-garden-muted truncate">
                    {row.designer_email}
                  </div>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  role="switch"
                  aria-checked={row.enabled}
                  disabled={busyId === row.id}
                  onClick={() => toggle(row, !row.enabled)}
                  title={row.enabled ? 'Turn off designer access' : 'Turn on designer access'}
                  className={
                    'relative h-6 w-11 rounded-full transition disabled:opacity-50 ' +
                    (row.enabled ? 'bg-garden-primary' : 'bg-garden-ink/20')
                  }
                >
                  <span
                    className={
                      'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ' +
                      (row.enabled ? 'translate-x-5' : 'translate-x-0')
                    }
                  />
                </button>
                <button
                  type="button"
                  onClick={() => remove(row)}
                  disabled={busyId === row.id}
                  className="text-[11px] text-garden-muted underline disabled:opacity-50"
                  title="Remove this designer's access entirely"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function Section({ title, description, children }) {
  return (
    <div>
      <h2 className="text-sm font-semibold text-garden-ink">{title}</h2>
      {description ? (
        <p className="text-xs text-garden-muted mt-0.5 mb-2">{description}</p>
      ) : null}
      <div className="bg-white rounded-2xl border border-garden-ink/10 p-3 space-y-4">
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-garden-muted mb-1">{label}</label>
      {children}
    </div>
  );
}

function FieldHint({ children }) {
  return <p className="text-[11px] text-garden-muted mt-1">{children}</p>;
}

function ToggleRow({ label, description, value, onChange, disabled }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-garden-ink">{label}</div>
        {description ? (
          <div className="text-xs text-garden-muted mt-0.5">{description}</div>
        ) : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        disabled={disabled}
        onClick={() => onChange(!value)}
        className={
          'relative h-6 w-11 rounded-full transition disabled:opacity-50 ' +
          (value ? 'bg-garden-primary' : 'bg-garden-ink/20')
        }
      >
        <span
          className={
            'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ' +
            (value ? 'translate-x-5' : 'translate-x-0')
          }
        />
      </button>
    </div>
  );
}
